import * as React from "react";
import { FC, useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import classNames from "classnames";
import { connect, Dispatch } from "react-redux";
import { format } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import type { IScope } from "reducers/scope";
import theme from "styles/theme";
import Grid from "@material-ui/core/Grid";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { MediaDialog } from "components/dialog/media-dialog";
import { IStore } from "reducers";
import { IItemRecord } from "records/item/item";
import Reminders from "components/product-media/components/reminders";
import Uploader from "components/product-media/components/uploader";
import ImageIcon from "components/icons/image-outlined";
import { NotificationError, NotificationSuccess } from "components/notification-banners";
import { MEDIA_TYPE_VIDEO } from "constants/media";
import { IAsset } from "constants/assets";
import { ContentLoading } from "components/content-loading";
import { DATATEST } from "constants/integration";
import { IMediaAsset, IMediaAssetMap } from "constants/media-asset";
import {
    fetchMediaAssets,
    mutateMediaAssets,
    resetProductMediaAssets,
    resetProductMedia,
    uploadVideo,
    save,
} from "actions/product-media-actions";
import { createNewMediaKey, transformMediaMap } from "helpers/manage-media";
import { IProductMedia } from "constants/product-media";

// prettier-ignore
interface IProps {
    product: IItemRecord;
    open: boolean;
    onClose(reload?: boolean): void; // prettier-ignore
}

interface IReduxProps {
    asset: IAsset;
    error: string | Error;
    isUploading: boolean;
    mediaAssetMap: IMediaAssetMap;
    revision: string;
    isUpdating: boolean;
    isFetching: boolean;
    scope: IScope;
}

interface IDispatchProps {
    resetProductMedia(): void;
    fetchProductMedia(id: string): void;
    onUpdate(product: string, assets: string[]): void;
    updateMediaAssets(productId: string, revision: string, mediaMap: IMediaAssetMap): void;
    onUpload(product: string, id: string): void; // prettier-ignore
    onReset(): void; // prettier-ignore;
}

const UploadVideoDialog: FC<IProps & IReduxProps & IDispatchProps> = (props) => {
    const { product, open, onClose, scope } = props;
    const { asset, error, onReset, mediaAssetMap, revision } = props;
    const {
        onUpload,
        isUploading,
        isUpdating,
        isFetching,
        fetchProductMedia,
        resetProductMedia,
        updateMediaAssets,
        onUpdate,
    } = props;

    const mediaMap = useMemo(() => transformMediaMap(mediaAssetMap), [mediaAssetMap]);

    const [directUploading, setDirectUploading] = useState<boolean>(false); // internal flag of uploader processing
    const [uploadError, setUploadError] = useState<string>(null);
    const [uploadSuccess, setUploadSuccess] = useState<string>(null);
    const [close, setClose] = useState<boolean>(false); // close the dialog right after update is finished

    // Additional settings are temporary disabled
    // const [autoplay, setAutoplay] = useState<boolean>(false);
    // const [mute, setMute] = useState<boolean>(false);
    const success = asset && !(directUploading || isUploading);

    const useUnlimitedImages = useMemo(() => scope.brand?.getPreference?.("unlimited_images"), [scope]);
    const media = useMemo<IProductMedia>(
        () => _.find(product.getMedia(scope), { type: MEDIA_TYPE_VIDEO }),
        [product, scope],
    );
    const errorMessage = useMemo(
        () => format("There were issues with the video you uploaded. Make sure they are the correct file type_size."),
        [],
    );

    useEffect(() => {
        fetchProductMedia(product._id);
    }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => setUploadError(error && errorMessage), [error, errorMessage]);

    const handleClose = useCallback(
        (reload?: boolean) => {
            onReset(); // reset uploading state
            resetProductMedia();
            onClose(reload);
            setUploadError(null);
            setUploadSuccess(null);
        },
        [onClose, onReset, resetProductMedia],
    );

    const handleUpload = useCallback(
        (response: { id: string }) => {
            if (response) {
                onUpload(product._id, response.id);
            }
        },
        [product, onUpload],
    );

    const handleSave = useCallback(() => {
        if (asset) {
            if (useUnlimitedImages) {
                const assetKey = createNewMediaKey(mediaMap);
                updateMediaAssets(product._id, revision, {
                    ...mediaMap,
                    [assetKey]: { ...asset, type: MEDIA_TYPE_VIDEO } as IMediaAsset,
                });
            } else {
                onUpdate(product._id, product.getMediaUpdate(MEDIA_TYPE_VIDEO, asset.asset_id));
            }
            setClose(true);
        } else {
            handleClose();
        }
    }, [product, asset, revision, mediaMap, updateMediaAssets, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCloseDialog = useCallback(() => handleClose(false), [handleClose]);

    const handleDelete = useCallback(() => {
        onUpdate(product._id, product.getMediaUpdate(MEDIA_TYPE_VIDEO));
        setClose(true);
    }, [product, onUpdate]);

    const handleUploadStart = useCallback(() => {
        setUploadError(null);
        setUploadSuccess(null);
    }, []);

    const handleError = useCallback(
        ({ message }: { message: string } | Error) => setUploadError(message || errorMessage),
        [errorMessage],
    );
    const handleHideError = useCallback(() => setUploadError(null), []);
    const handleReject = useCallback(() => setUploadError(errorMessage), [errorMessage]);

    const handleSuccess = useCallback(
        ({ message }: { message: string } | Error) => setUploadSuccess(message || errorMessage),
        [errorMessage],
    );
    const handleHideSuccess = useCallback(() => setUploadSuccess(null), []);

    // Additional settings are temporary disabled
    // const handleToggle = useCallback((setter: (value: boolean) => void) => (e, value: boolean) => setter(value), []);

    useEffect(() => {
        if (close && !isUpdating) {
            if (!error) {
                handleClose(true);
            } else {
                setClose(false);
            }
        }
    }, [close, isUpdating, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (success) {
            handleSuccess({ message: format("|media| was uploaded successfully", { media: format("Video") }) });
        }
    }, [success, uploadError, handleSuccess]);

    const classes = useStyles({});
    return (
        <MediaDialog
            open={open}
            title={format("Add Video")}
            onClose={handleCloseDialog}
            onSave={handleSave}
            onDelete={!!media && !useUnlimitedImages && handleDelete}
            disabled={directUploading || isUploading || isUpdating || isFetching}
        >
            <div className={classes.contentTitle}>
                {format("Review the specifications and select your video file to upload")}
            </div>
            <NotificationError classes={{ root: classes.error }} message={uploadError} onClose={handleHideError} />
            <NotificationSuccess
                classes={{ root: classes.error }}
                message={uploadSuccess}
                onClose={handleHideSuccess}
            />
            <Grid className={classes.gridRoot} container={true} wrap="wrap" justify="space-around" spacing={2}>
                <Grid className={classes.margin} item={true}>
                    <Uploader
                        inputProps={{
                            "data-test": DATATEST.MEDIA_DIALOG.VIDEO_UPLOADER,
                        }}
                        accept="video/mp4,video/quicktime,./mp4,.mov"
                        onUpload={handleUpload}
                        onUploadError={handleError}
                        loading={directUploading || isUploading}
                        maxSize={1024 * 1024 * 1024}
                        onDropReject={handleReject}
                        onUploadStart={handleUploadStart}
                        onLoading={setDirectUploading}
                        scope={scope}
                    >
                        {success && <ImageIcon className={classNames(classes.icon, classes.success)} />}
                        {!success && <ImageIcon className={classes.icon} />}
                        <div className={classes.dragLabel}>
                            <FormatMessage
                                path="Drag your video file here |br| or |span|click here|span| to select"
                                elements={{ br: <br />, span: <span className={classes.click} /> }}
                            />
                        </div>
                    </Uploader>
                </Grid>
                <Grid
                    className={classes.margin}
                    data-test={DATATEST.MEDIA_DIALOG.VIDEO_SPECIFICATIONS}
                    item={true}
                    xs={true}
                >
                    <Reminders title={format("Video specifications:")}>
                        <li>{format("MP4_MOV only")}</li>
                        <li>
                            {format(
                                "Dimensions: 1920 x 1080 recommended (other ratios allowed, ex. square or vertical)",
                            )}
                        </li>
                        <li>{format("Maximum file size |size|", { size: "500 MB" })}</li>
                        <li>{format("Minimum video length: 3 seconds")}</li>
                    </Reminders>
                </Grid>
                {/*// Additional settings are temporary disabled*/}
                {/*<Grid className={classes.margin} item={true} xs={12}>*/}
                {/*    <FormGroup>*/}
                {/*        <FormControlLabel*/}
                {/*            classes={{ root: classes.formControl, label: classes.formLabel }}*/}
                {/*            control={<CustomToggle checked={mute} onChange={handleToggle(setMute)}/>}*/}
                {/*            label={format("Muted")}*/}
                {/*        />*/}
                {/*        <FormControlLabel*/}
                {/*            classes={{ root: classes.formControl, label: classes.formLabel }}*/}
                {/*            control={<CustomToggle checked={autoplay} onChange={handleToggle(setAutoplay)}/>}*/}
                {/*            label={format("Autoplay")}*/}
                {/*        />*/}
                {/*    </FormGroup>*/}
                {/*</Grid>*/}
            </Grid>
            {isUpdating && <ContentLoading className={classes.loading} />}
        </MediaDialog>
    );
};

const useStyles = makeStyles({
    contentTitle: {
        fontSize: 14,
        padding: 10,
    },
    click: {
        textDecoration: "underline",
    },
    icon: {
        width: 160,
        height: 160,
        padding: 30,
        color: theme.palette.grey,
    },
    dragLabel: {
        fontSize: 14,
        textAlign: "center",
        lineHeight: "1.8em",
        color: theme.palette.darkGrey,
    },
    gridRoot: {
        marginTop: 15,
    },
    margin: {
        margin: "5px 20px",
        "@media(max-width: 600px)": {
            margin: 10,
        },
    },
    error: {
        margin: 10,
    },
    formControl: {
        margin: "5px 0",
    },
    formLabel: {
        fontFamily: "inherit",
        fontSize: 14,
        marginLeft: 10,
    },
    loading: {
        left: -24,
    },
    success: {
        color: theme.palette.accent1Color,
    },
});

const mapStateToProps = (store: IStore): IReduxProps => ({
    asset: store.productMedia.payload,
    error: store.productMedia.error,
    isUploading: store.productMedia.isUploading,
    mediaAssetMap: store.productMediaAssets?.payload?.media as IMediaAssetMap,
    isFetching: store.productMediaAssets?.isFetching,
    isUpdating: store.productMediaAssets?.isUpdating,
    revision: store.productMediaAssets?.payload?.revision,
    scope: store.scope,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    fetchProductMedia: (productId: string) => dispatch(fetchMediaAssets(productId)),
    resetProductMedia: () => dispatch(resetProductMediaAssets()),
    onUpdate: (product: string, assets: string[]) => dispatch(save(product, assets)),
    updateMediaAssets: (productId: string, revision: string, mediaMap: IMediaAssetMap) =>
        dispatch(mutateMediaAssets(productId, revision, mediaMap)),
    onUpload: (product: string, id: string) => dispatch(uploadVideo(product, id)),
    onReset: () => dispatch(resetProductMedia()),
});

export default connect<IReduxProps, IDispatchProps, IProps>(mapStateToProps, mapDispatchToProps)(UploadVideoDialog);
